import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Mail, Phone, MapPin, Clock, Send, MessageSquare, 
  Headphones, CheckCircle, ArrowRight, Building2,
  Facebook, Instagram, Twitter, Linkedin
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setError('Mesaj gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Telefon',
      value: '0850 242 78 08',
      subtext: 'Pazartesi - Cumartesi: 08:00 - 20:00',
      action: 'tel:08502427808',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Mail,
      title: 'E-posta',
      value: 'destek@hizmetgeliyo.com',
      subtext: 'En geç 24 saat içinde yanıt',
      action: 'mailto:destek@hizmetgeliyo.com',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      value: '7/24 Acil Servis',
      subtext: 'Her zaman yanınızdayız',
      action: null,
      color: 'from-amber-500 to-orange-600'
    }
  ];

  const subjects = [
    'Genel Bilgi',
    'Hizmet Talebi',
    'Şikayet / Öneri',
    'İş Ortaklığı',
    'Usta Başvurusu',
    'Diğer'
  ];

  const faqs = [
    { q: 'Hizmet almak için ne yapmalıyım?', a: 'Hizmetler sayfasından ihtiyacınız olan hizmeti seçip form doldurmanız yeterli.' },
    { q: 'Ödeme nasıl yapılır?', a: 'Ödeme, hizmet tamamlandıktan sonra nakit veya kredi kartı ile yapılabilir.' },
    { q: 'Garanti süresi ne kadar?', a: 'Tüm hizmetlerimiz 30 gün garanti kapsamındadır.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-[#006D77] via-[#0A9396] to-[#006D77] overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        
        <div className="max-w-6xl mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-5 py-2.5 mb-6">
            <Headphones className="w-5 h-5 text-white" />
            <span className="text-white font-semibold">7/24 Destek</span>
          </div>
          
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Bizimle İletişime<br />
            <span className="text-[#F6AD55]">Geçin</span>
          </h1>
          
          <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto">
            Sorularınız, önerileriniz veya şikayetleriniz için her zaman yanınızdayız
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-12 md:py-16 -mt-16 relative z-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {contactInfo.map((info, idx) => {
              const Icon = info.icon;
              const Wrapper = info.action ? 'a' : 'div';
              
              return (
                <Wrapper
                  key={idx}
                  href={info.action || undefined}
                  className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 group"
                  data-testid={`contact-card-${idx}`}
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${info.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">{info.title}</h3>
                  <p className="text-[#006D77] font-semibold mb-1">{info.value}</p>
                  <p className="text-gray-500 text-sm">{info.subtext}</p>
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form Section */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-[#006D77] to-[#0A9396] p-6 md:p-8">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                      <MessageSquare className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-white">Bize Mesaj Gönderin</h2>
                      <p className="text-white/70">En kısa sürede yanıt vereceğiz</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 md:p-8">
                  {submitted ? (
                    <div className="text-center py-12" data-testid="contact-success">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-500" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Mesajınız Alındı!</h3>
                      <p className="text-gray-600 mb-6 max-w-md mx-auto">
                        En kısa sürede sizinle iletişime geçeceğiz. Teşekkür ederiz.
                      </p>
                      <Button
                        onClick={() => {
                          setSubmitted(false);
                          setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                        }}
                        className="bg-[#006D77] hover:bg-[#005a63] text-white rounded-xl px-6"
                      >
                        Yeni Mesaj Gönder
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                      <div className="grid md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Adınız Soyadınız <span className="text-red-500">*</span>
                          </label>
                          <Input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            className="rounded-xl border-gray-200 h-12"
                            placeholder="Ahmet Yılmaz"
                            data-testid="contact-name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            E-posta <span className="text-red-500">*</span>
                          </label>
                          <Input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            className="rounded-xl border-gray-200 h-12"
                            placeholder="ornek@email.com"
                            data-testid="contact-email"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Telefon
                          </label>
                          <Input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                            className="rounded-xl border-gray-200 h-12"
                            placeholder="05XX XXX XX XX"
                            data-testid="contact-phone"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Konu <span className="text-red-500">*</span>
                          </label>
                          <select
                            required
                            value={formData.subject}
                            onChange={(e) => setFormData({...formData, subject: e.target.value})}
                            className="w-full rounded-xl border border-gray-200 h-12 px-4 text-sm focus:border-[#006D77] focus:ring-[#006D77]"
                            data-testid="contact-subject"
                          >
                            <option value="">Seçiniz...</option>
                            {subjects.map((sub, i) => (
                              <option key={i} value={sub}>{sub}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Mesajınız <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          required
                          rows={5}
                          value={formData.message}
                          onChange={(e) => setFormData({...formData, message: e.target.value})}
                          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm resize-none focus:border-[#006D77] focus:ring-[#006D77]"
                          placeholder="Mesajınızı buraya yazın..."
                          data-testid="contact-message"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-[#006D77] to-[#0A9396] hover:from-[#005a63] hover:to-[#088488] text-white py-4 rounded-xl text-lg font-semibold"
                        data-testid="contact-submit"
                      >
                        {loading ? (
                          <div className="flex items-center justify-center gap-3">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Gönderiliyor...</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-3">
                            <Send className="w-5 h-5" />
                            <span>Mesaj Gönder</span>
                          </div>
                        )}
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              {/* FAQ */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#006D77]" />
                  Sık Sorulan Sorular
                </h3>
                <div className="space-y-4">
                  {faqs.map((faq, idx) => (
                    <div key={idx} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                      <h4 className="font-semibold text-gray-900 text-sm mb-1">{faq.q}</h4>
                      <p className="text-gray-500 text-sm">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-gradient-to-br from-[#006D77] to-[#0A9396] rounded-2xl p-6 text-white">
                <h3 className="text-lg font-bold mb-4">Hızlı Erişim</h3>
                <div className="space-y-3">
                  <a 
                    href="/hizmetler"
                    className="flex items-center justify-between bg-white/10 hover:bg-white/20 rounded-xl p-3 transition-colors"
                  >
                    <span className="font-medium">Hizmet Al</span>
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <a 
                    href="/garanti"
                    className="flex items-center justify-between bg-white/10 hover:bg-white/20 rounded-xl p-3 transition-colors"
                  >
                    <span className="font-medium">Garanti Bilgisi</span>
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <a 
                    href="/neden-bize-katil"
                    className="flex items-center justify-between bg-white/10 hover:bg-white/20 rounded-xl p-3 transition-colors"
                  >
                    <span className="font-medium">Usta Ol</span>
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Sosyal Medya</h3>
                <p className="text-gray-500 text-sm mb-4">Bizi takip edin, güncel kalın</p>
                <div className="flex gap-3">
                  {[
                    { icon: Facebook, color: 'bg-blue-500', href: '#' },
                    { icon: Instagram, color: 'bg-gradient-to-br from-purple-500 to-pink-500', href: '#' },
                    { icon: Twitter, color: 'bg-sky-500', href: '#' },
                    { icon: Linkedin, color: 'bg-blue-700', href: '#' },
                  ].map((social, idx) => {
                    const SocialIcon = social.icon;
                    return (
                      <a
                        key={idx}
                        href={social.href}
                        className={`w-12 h-12 ${social.color} rounded-xl flex items-center justify-center text-white hover:opacity-80 transition-opacity`}
                      >
                        <SocialIcon className="w-5 h-5" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
